<template>
  <div class="pagination-container">
    <div class="text-sm text-gray-600">
      Showing {{ startItem }}-{{ endItem }} of {{ totalItems }} tutors
    </div>
    <div class="pagination-buttons">
      <button 
        class="pagination-button"
        :disabled="currentPage <= 1"
        @click="$emit('pageChange', currentPage - 1)"
      >
        <ChevronLeftIcon class="chevron-left w-4 h-4" />
        Previous
      </button>
      
      <div class="page-numbers">
        <button
          v-for="page in visiblePages"
          :key="page"
          class="pagination-page"
          :class="{ 'bg-blue-600': page === currentPage }"
          @click="$emit('pageChange', page)"
        >
          {{ page }}
        </button>
      </div>
      
      <button 
        class="pagination-button"
        :disabled="currentPage >= totalPages"
        @click="$emit('pageChange', currentPage + 1)"
      >
        Next
        <ChevronRightIcon class="chevron-right w-4 h-4" />
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/solid'

const props = defineProps({
  currentPage: {
    type: Number,
    default: 1
  },
  totalPages: {
    type: Number,
    default: 1
  },
  itemsPerPage: {
    type: Number,
    default: 4
  },
  totalItems: {
    type: Number,
    default: 0
  }
})

defineEmits(['pageChange', 'itemsPerPageChange'])

const startItem = computed(() => {
  if (props.totalItems === 0) return 0
  return ((props.currentPage - 1) * props.itemsPerPage) + 1
})

const endItem = computed(() => {
  return Math.min(props.currentPage * props.itemsPerPage, props.totalItems)
})

const visiblePages = computed(() => {
  const pages = []
  const maxVisible = 5
  
  if (props.totalPages <= maxVisible) {
    for (let i = 1; i <= props.totalPages; i++) {
      pages.push(i)
    }
  } else {
    const start = Math.max(1, props.currentPage - 2)
    const end = Math.min(props.totalPages, props.currentPage + 2)
    
    for (let i = start; i <= end; i++) {
      pages.push(i)
    }
  }
  
  return pages
})
</script>

<style scoped>
.pagination-container {
  margin-top: 32px;
  background-color: transparent;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.text-sm {
  color: #6B7280;
  font-size: 14px;
  font-weight: 500;
}

.text-gray-600 {
  color: #4b5563;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pagination-button {
  font-size: 14px;
  line-height: 1.4rem;
  font-weight: 500;
  min-width: 85px;
  border: 2px solid #155DFC;
  text-align: center;
  padding: 10px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  transition: all 0.2s ease;
  border-radius: 5px;
  color: #155DFC;
  cursor: pointer;
  gap: 8px;
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #F3F4F6;
}

.pagination-button:not(:disabled):hover {
  background-color: #f8f9fa;
  transform: translateY(-1px);
}

.page-numbers {
  display: flex;
  gap: 2px;
}

.pagination-page {
  font-size: 14px;
  line-height: 1.5rem;
  font-weight: 500;
  min-width: 38px;
  height: 38px;
  text-align: center;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #155DFC;
  border-radius: 5px;
  color: #155DFC;
  background-color: white;
  cursor: pointer;
}

.pagination-page:hover {
  background-color: #F3F4F6;
  transform: translateY(-1px);
}

.pagination-page.bg-blue-600 {
  background-color: #2563EB;
  color: white;
}

.pagination-page.bg-blue-600:hover {
  background-color: #1D4ED8;
}

.chevron-left,
.chevron-right {
  flex-shrink: 0;
}

.w-4 {
  width: 1rem;
}

.h-4 {
  height: 1rem;
}

@media (max-width: 640px) {
  .pagination-container {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }

  .pagination-container .text-sm {
    text-align: center;
    width: 100%;
  }
  
  .pagination-buttons {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .pagination-button {
    min-width: 75px;
    font-size: 12px;
  }
  
  .pagination-page {
    min-width: 32px;
    height: 32px;
    font-size: 12px;
  }
}
</style>
